// useGeolocation — wrapper around navigator.geolocation.watchPosition.
// Local only — no Firestore writes. iOS Safari requires HTTPS in prod.

import { useEffect, useState } from "react";

export interface GeolocationState {
  coords: { lat: number; lng: number } | null;
  accuracy: number | null;
  status: "idle" | "watching" | "denied" | "unsupported" | "error";
  error: string | null;
}

const INITIAL: GeolocationState = {
  coords: null,
  accuracy: null,
  status: "idle",
  error: null,
};

export function useGeolocation(enabled: boolean = true): GeolocationState {
  const [state, setState] = useState<GeolocationState>(INITIAL);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState({ ...INITIAL, status: "unsupported" });
      return;
    }
    setState((s) => ({ ...s, status: "watching" }));

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setState({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          accuracy: pos.coords.accuracy,
          status: "watching",
          error: null,
        });
      },
      (err) => {
        // 2026-05-30 現場修:舊機(iPhone SE 2)在山區 / 弱訊號第一次鎖定常
        // 需 20-40s,原本 15s timeout 一到就 error + 清空 coords → 點永遠
        // 出不來。改法:
        //  - PERMISSION_DENIED → denied(真的被拒,顯示提示)。
        //  - TIMEOUT / POSITION_UNAVAILABLE → 不要放棄、不要清掉已有的
        //    coords(若之前鎖到過就留著),維持 "watching"(顯示「尋找位置
        //    中…」),watchPosition 會繼續嘗試直到鎖到。
        if (err.code === err.PERMISSION_DENIED) {
          setState({
            coords: null,
            accuracy: null,
            status: "denied",
            error: err.message,
          });
          return;
        }
        // timeout / unavailable:保留先前 coords、繼續找,不顯示錯誤。
        setState((s) => ({
          ...s,
          status: "watching",
          error: err.message,
        }));
      },
      // timeout 從 15s 拉到 45s(舊機弱訊號要更久);maximumAge 0 確保拿新鮮
      // 定位、不吃太舊的快取點。
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 45000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled]);

  return state;
}
